import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCamera, IconTrash } from '@tabler/icons-react';
import { TeeAvatar } from '@/components/ui/tee-avatar';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeConfirmationDialog } from '@/components/ui/tee-confirmation-dialog';
import AvatarZoomModal from '@/components/ui/avatar-zoom-modal';

/** Mirrors the API's own limits so the user gets told before a wasted upload. */
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

interface ProfilePhotoEditorProps {
  photoUrl?: string;
  initials: string;
  isUploading: boolean;
  isRemoving: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
}

/**
 * Avatar with change / remove controls.
 *
 * The client app offers "Take Photo" alongside "Choose from Library", routed
 * through its React Native WebView bridge. box-os runs in a plain browser with
 * no such bridge, so that option is deliberately absent — a file picker already
 * exposes the camera on mobile browsers, and on desktop the option would be
 * dead.
 */
export function ProfilePhotoEditor({
  photoUrl,
  initials,
  isUploading,
  isRemoving,
  onUpload,
  onRemove,
}: Readonly<ProfilePhotoEditorProps>) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The picked file is held here until the user has framed it; only the cropped
  // result is uploaded, so nobody ends up with an off-centre avatar they then
  // have to re-upload to fix.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [zoomOpen, setZoomOpen] = useState(false);

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset immediately so picking the same file twice still fires onChange.
    event.target.value = '';
    if (!file) return;

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setError(t('profile.photoTypeError'));
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setError(t('profile.photoSizeError'));
      return;
    }
    setError(null);
    setPendingFile(file);
    setZoomOpen(true);
  };

  const closeZoom = () => {
    setZoomOpen(false);
    setPendingFile(null);
  };

  const busy = isUploading || isRemoving;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-4">
        <TeeAvatar
          size="100px"
          style={photoUrl ? 'Image' : 'Text'}
          type="Forest"
          initials={initials}
          src={photoUrl}
        />

        <div className="flex flex-col gap-2 min-w-0">
          <div className="flex flex-wrap gap-2">
            <TeeButton
              variant="secondary"
              size="s"
              disabled={busy}
              leftIcon={<IconCamera size={16} />}
              onClick={() => fileInputRef.current?.click()}
            >
              {isUploading
                ? t('profile.photoUploading')
                : photoUrl
                  ? t('profile.photoChange')
                  : t('profile.photoAdd')}
            </TeeButton>

            {photoUrl && (
              <TeeButton
                variant="destructive"
                size="s"
                disabled={busy}
                leftIcon={<IconTrash size={16} />}
                onClick={() => setConfirmRemove(true)}
              >
                {isRemoving ? t('profile.photoRemoving') : t('profile.photoRemove')}
              </TeeButton>
            )}
          </div>
          <p className="text-body-xs text-[var(--text-content-sub-title)]">
            {t('profile.photoHint')}
          </p>
        </div>
      </div>

      {error && <p className="text-body-xs text-[var(--text-badge-red)]">{error}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_MIME_TYPES.join(',')}
        className="hidden"
        onChange={handleFile}
      />

      {/* Zoom/crop before upload — the same editor the admin photo form uses,
          so both paths produce identically framed images. */}
      <AvatarZoomModal
        isOpen={zoomOpen}
        imageFile={pendingFile}
        onClose={closeZoom}
        onSave={(blob) => {
          closeZoom();
          // The editor always outputs JPEG, so name and type must agree with
          // what it produced rather than with the file the user picked.
          onUpload(new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' }));
        }}
      />

      <TeeConfirmationDialog
        isOpen={confirmRemove}
        title={t('profile.photoRemoveTitle')}
        description={t('profile.photoRemoveBody')}
        variant="destructive"
        confirmText={t('profile.photoRemove')}
        cancelText={t('common.cancel')}
        isLoading={isRemoving}
        onConfirm={() => {
          setConfirmRemove(false);
          onRemove();
        }}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}
